import React, { useState } from "react";

import {
  Select,
  MenuItem,
  TextareaAutosize,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  useMediaQuery,
  DialogContentText
} from "@material-ui/core";

import { LifvsUser } from "../../../models/User";
import * as UserApi from "../../../api/users";
import Modal from "../../../components/dialogs/modal";
import { useI18n } from "../../../hooks/useI18n";

import useStyles from "./styles";
import { useTheme } from "@material-ui/core/styles";

interface FormValues {
  [index: string]: string;

  lockoutReason: string;
  comment: string;
}

export interface LockUserModalProps {
  readonly user: LifvsUser;
  readonly visible: boolean;
  readonly onCloseDialog: (lockoutReason?: string) => void;
}

const LockUserModal = ({
  user,
  visible,
  onCloseDialog
}: LockUserModalProps) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const errorMessages: { [index: string]: string } = {
    lockoutReason: t("common.pleaseChooseReason"),
    comment: t("common.enterComment")
  };

  const validate = (values: FormValues) => {
    const errors: { [index: string]: string } = {};
    const requiredFields = Object.keys(errorMessages);

    requiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = errorMessages[field];
      }
    });

    return errors;
  };

  const [formValues, setFormValues] = useState<FormValues>({
    lockoutReason: "",
    comment: ""
  });

  const [errors, setErrors] = useState<{ [index: string]: string }>({});

  const isValid = (validationResult: { [index: string]: string }) =>
    Object.keys(validationResult).length === 0;

  const onLock = () => {
    const validationResult = validate(formValues);

    if (isValid(validationResult)) {
      return UserApi.lock(
        user.id,
        formValues.lockoutReason,
        formValues.comment
      ).then(() => onCloseDialog(formValues.lockoutReason));
    } else {
      setErrors(validationResult);
    }
  };

  return (
    <Dialog
      onClose={() => onCloseDialog(undefined)}
      open={visible}
      fullWidth={true}
      maxWidth={"sm"}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>{t("common.lockUser")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("common.enterReasonAndCommentToLock")}{" "}
          <strong>
            {user.firstName} {user.lastName}
          </strong>
        </DialogContentText>
        <FormControl
          variant={"standard"}
          style={{
            minWidth: 120,
            width: mobile ? "100%" : "80%",
            marginBottom: 15
          }}
        >
          <InputLabel id={"reason-label"}>{t("common.reason")}</InputLabel>
          <Select
            labelId={"reason-label"}
            label={t("common.reason")}
            error={errors["lockoutReason"] !== undefined}
            value={formValues?.lockoutReason}
            onChange={({ target: { value } }) =>
              setFormValues({ ...formValues, lockoutReason: value as string })
            }
          >
            <MenuItem value={"Stealing"}>{t("common.stealing")}</MenuItem>
            <MenuItem value={"Other"}>{t("common.other")}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={t("common.comment")}
          multiline
          rows={5}
          style={{
            minWidth: 120,
            width: mobile ? "100%" : "80%",
            marginBottom: 15
          }}
          value={formValues?.comment}
          error={errors["comment"] !== undefined}
          onChange={({ target: { value } }) =>
            setFormValues({ ...formValues, comment: value as string })
          }
        />
      </DialogContent>
      <DialogActions>
        <Button color={"primary"} onClick={() => onCloseDialog(undefined)}>
          {t("common.actions.cancel")}
        </Button>
        <Button color={"primary"} onClick={onLock}>
          {t("common.lockUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LockUserModal;
